import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Building2,
    CheckSquare,
    AlertOctagon,
    CalendarRange,
    Sparkles,
    ArrowUpRight,
    BedDouble,
} from "lucide-react";
import api from "@/lib/api";
import KPICard from "@/components/KPICard";
import StateBadge from "@/components/StateBadge";
import {
    PROPERTY_STATE_LABELS,
    TASK_STATE_LABELS,
    INCIDENT_STATE_LABELS,
    TASK_TYPE_LABELS,
    INCIDENT_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

export default function PortalDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [client, setClient] = useState(null);
    const [props, setProps] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        Promise.all([
            api.get("/portal/dashboard"),
            api.get("/portal/me"),
            api.get("/portal/properties"),
        ])
            .then(([d, m, p]) => {
                if (!active) return;
                setData(d.data);
                setClient(m.data?.client || null);
                setProps(p.data || []);
            })
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []);

    if (loading || !data) {
        return (
            <div className="space-y-6" data-testid="portal-dashboard-loading">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="zentra-skeleton h-28" />
                    ))}
                </div>
                <div className="zentra-skeleton h-72" />
            </div>
        );
    }

    const propsById = Object.fromEntries(props.map((p) => [p.id, p]));
    const k = data.kpis;

    return (
        <div className="space-y-8" data-testid="portal-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        {client?.name || "Tu cuenta"} ·{" "}
                        {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Hola, {user?.name?.split(" ")[0] || "Cliente"}.
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Estado en tiempo real de tus {k.active_properties} propiedades en gestión.
                    </p>
                </div>
                <Link to="/portal/reportes" className="zentra-btn-primary" data-testid="portal-cta-reports">
                    Ver informe mensual
                    <ArrowUpRight size={14} />
                </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="portal-kpi-grid">
                <KPICard
                    title="Propiedades"
                    value={k.active_properties}
                    suffix="en gestión"
                    icon={Building2}
                    hint={`Ocupación ${k.occupancy_ratio}%`}
                    accent
                    testId="portal-kpi-properties"
                />
                <KPICard
                    title="Limpiezas hoy"
                    value={k.tasks_today}
                    suffix="programadas"
                    icon={Sparkles}
                    hint={`${k.tasks_completed_today} ya completadas`}
                    testId="portal-kpi-cleanings"
                />
                <KPICard
                    title="Próxima semana"
                    value={k.tasks_upcoming_week}
                    suffix="servicios"
                    icon={CalendarRange}
                    hint="Limpiezas y revisiones"
                    testId="portal-kpi-upcoming"
                />
                <KPICard
                    title="Incidencias"
                    value={k.incidents_open}
                    suffix="abiertas"
                    icon={AlertOctagon}
                    hint={k.incidents_critical > 0 ? `${k.incidents_critical} críticas` : "Sin urgencias"}
                    testId="portal-kpi-incidents"
                />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Properties by state */}
                <div className="zentra-card-static p-5 lg:col-span-1" data-testid="portal-states">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Estado de tus propiedades</h3>
                        <Link to="/portal/propiedades" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Ver todas
                        </Link>
                    </div>
                    {Object.entries(data.properties_by_state || {}).filter(([, v]) => v > 0).length === 0 ? (
                        <p className="text-sm text-zentra-muted py-6 text-center">Aún sin actividad.</p>
                    ) : (
                        <div className="space-y-2">
                            {Object.entries(data.properties_by_state || {})
                                .filter(([, v]) => v > 0)
                                .sort((a, b) => b[1] - a[1])
                                .map(([state, count]) => (
                                    <div
                                        key={state}
                                        className="flex items-center justify-between py-1.5 border-b border-zentra-border last:border-0"
                                    >
                                        <StateBadge value={state} dict={PROPERTY_STATE_LABELS} />
                                        <span className="font-display font-semibold text-zentra-ink">{count}</span>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Upcoming tasks */}
                <div className="zentra-card-static p-5 lg:col-span-1" data-testid="portal-upcoming">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Próximas limpiezas</h3>
                        <Link to="/portal/calendario" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Calendario
                        </Link>
                    </div>
                    <ul className="divide-y divide-zentra-border">
                        {(data.upcoming_tasks || []).slice(0, 6).map((t) => {
                            const p = propsById[t.property_id];
                            return (
                                <li key={t.id} className="py-2.5 flex flex-col gap-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-medium text-sm text-zentra-ink truncate">
                                            {p?.name || "Propiedad"}
                                        </span>
                                        <StateBadge value={t.state} dict={TASK_STATE_LABELS} />
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-zentra-muted">
                                        <span>
                                            <CheckSquare size={11} className="inline mr-1" />
                                            {TASK_TYPE_LABELS[t.type] || t.type}
                                        </span>
                                        <span className="font-mono-zentra">{t.scheduled_date}</span>
                                    </div>
                                </li>
                            );
                        })}
                        {(data.upcoming_tasks || []).length === 0 && (
                            <li className="py-6 text-sm text-zentra-muted text-center">Sin limpiezas próximas.</li>
                        )}
                    </ul>
                </div>

                {/* Open incidents */}
                <div className="zentra-card-static p-5 lg:col-span-1" data-testid="portal-incidents">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Incidencias abiertas</h3>
                        <Link to="/portal/incidencias" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Ver todas
                        </Link>
                    </div>
                    <ul className="divide-y divide-zentra-border">
                        {(data.open_incidents || []).slice(0, 6).map((i) => {
                            const p = propsById[i.property_id];
                            return (
                                <li key={i.id} className="py-2.5 flex flex-col gap-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-medium text-sm text-zentra-ink truncate">{i.title}</span>
                                        {i.priority === "critical" && (
                                            <span className="w-2 h-2 rounded-full bg-red-600 zentra-pulse" />
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-zentra-muted truncate">
                                            {p?.code} · {INCIDENT_TYPE_LABELS[i.type] || i.type}
                                        </span>
                                        <StateBadge value={i.state} dict={INCIDENT_STATE_LABELS} />
                                    </div>
                                </li>
                            );
                        })}
                        {(data.open_incidents || []).length === 0 && (
                            <li className="py-6 text-sm text-zentra-muted text-center">Sin incidencias abiertas.</li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Properties strip */}
            {props.length > 0 && (
                <div data-testid="portal-properties-strip">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Tus propiedades</h3>
                        <Link to="/portal/propiedades" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Ver todas
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {props.slice(0, 8).map((p) => (
                            <Link
                                key={p.id}
                                to={`/portal/propiedades/${p.id}`}
                                className="zentra-card p-4 flex flex-col gap-2"
                                data-testid={`portal-property-card-${p.code}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                                            {p.code}
                                        </p>
                                        <h4 className="font-display font-semibold text-zentra-ink leading-tight line-clamp-1">
                                            {p.name}
                                        </h4>
                                    </div>
                                    <StateBadge value={p.state} dict={PROPERTY_STATE_LABELS} />
                                </div>
                                <p className="text-xs text-zentra-muted line-clamp-1">{p.address || "—"}</p>
                                <div className="flex items-center gap-2 text-[11px] text-zentra-muted mt-auto pt-2 border-t border-zentra-border">
                                    <BedDouble size={11} /> {p.bedrooms || 0} hab · {p.bathrooms || 0} baños · {p.capacity || 0} pax
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
